import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Heading,
  Text,
  Link as ChakraLink,
  useToast,
  useColorModeValue,
  Alert,
  AlertIcon,
  VStack,
  Code,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [hasConfirmedSaving, setHasConfirmedSaving] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (recoveryPhrase && !hasConfirmedSaving) {
      toast({
        title: 'Confirm recovery phrase',
        description: 'Please confirm that you have saved your recovery phrase',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (recoveryPhrase && hasConfirmedSaving) {
      navigate('/app/pricing');
      return;
    }

    setIsLoading(true);

    if (password !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        status: 'error',
        duration: 3000,
      });
      setIsLoading(false);
      return;
    }

    if (password.length < 12) {
      toast({
        title: 'Error',
        description: 'Password must be at least 12 characters long',
        status: 'error',
        duration: 3000,
      });
      setIsLoading(false);
      return;
    }

    try {
      const result = await register(email, password);
      setRecoveryPhrase(result.recoveryPhrase);
      onOpen(); // Show recovery phrase modal
    } catch (error) {
      toast({
        title: 'Registration failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRecovery = () => {
    if (!hasConfirmedSaving) {
      toast({
        title: 'Please confirm',
        description: 'You must confirm that you have saved your recovery phrase',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    onClose();
    navigate('/app/pricing');
  };

  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <Stack spacing="8">
        <Stack spacing="6">
          <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
            <Heading size={{ base: 'xs', md: 'sm' }}>Create your account</Heading>
            <Text color="muted">Start journaling with end-to-end encryption</Text>
          </Stack>
        </Stack>
        <Box
          py={{ base: '0', sm: '8' }}
          px={{ base: '4', sm: '10' }}
          bg={useColorModeValue('white', 'gray.700')}
          boxShadow={{ base: 'none', sm: 'md' }}
          borderRadius={{ base: 'none', sm: 'xl' }}
        >
          <form onSubmit={handleSubmit}>
            <Stack spacing="6">
              <Stack spacing="5">
                <FormControl isRequired>
                  <FormLabel htmlFor="name">Name</FormLabel>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel htmlFor="password">Password</FormLabel>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </FormControl>
              </Stack>
              <Alert status="info">
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <Text>Your password is used to encrypt your journal entries.</Text>
                  <Text>Make sure to use a strong password that you won't forget.</Text>
                  <Text>You will be shown a recovery phrase after registration.</Text>
                </VStack>
              </Alert>
              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                fontSize="md"
                isLoading={isLoading}
              >
                Create Account
              </Button>
            </Stack>
          </form>
        </Box>
        <Text textAlign="center">
          Already have an account?{' '}
          <ChakraLink as={RouterLink} to="/login" color="blue.500">
            Sign in
          </ChakraLink>
        </Text>
      </Stack>

      {/* Recovery Phrase Modal */}
      <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save Your Recovery Phrase</ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="warning">
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">
                    Write down these 12 words and keep them safe!
                  </Text>
                  <Text>
                    This is your recovery phrase. It's the only way to recover your account if you
                    forget your password.
                  </Text>
                  <Text>
                    Never share these words with anyone. Anyone with this phrase can access your
                    journal.
                  </Text>
                </VStack>
              </Alert>

              <Box
                p={4}
                bg={useColorModeValue('gray.50', 'gray.700')}
                borderRadius="md"
                width="full"
              >
                <Code fontSize="md" width="full" p={4}>
                  {recoveryPhrase}
                </Code>
              </Box>

              <Checkbox
                isRequired
                onChange={(e) => setHasConfirmedSaving(e.target.checked)}
              >
                I have written down my recovery phrase and stored it in a safe place
              </Checkbox>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleConfirmRecovery}>
              I've Saved My Recovery Phrase
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default RegisterPage; 